import { useEffect, useRef } from "react";
import { FaShieldAlt, FaBolt, FaUserCheck, FaAward, FaCheckCircle } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const WhyChooseUs = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const features = [
    {
      icon: <FaUserCheck />,
      title: "User-Centric Journey",
      description: "We prioritize our clients' needs, providing a seamless, 100% digital journey designed for convenience and speed.",
      color: "var(--primary)",
      glow: "rgba(26, 47, 90, 0.2)",
    },
    {
      icon: <FaShieldAlt />,
      title: "Registered NBFC",
      description: `${import.meta.env.VITE_COMPANY_NAME} is an RBI-registered NBFC — delivering capital solutions with high trust and regulatory compliance.`,
      color: "var(--secondary)",
      glow: "rgba(212, 165, 74, 0.2)",
    },
    {
      icon: <FaAward />,
      title: "Absolute Transparency",
      description: "No hidden charges. No complex conditions. Every term is communicated with complete transparency.",
      color: "var(--primary)",
      glow: "rgba(26, 47, 90, 0.2)",
    },
    {
      icon: <FaBolt />,
      title: "Rapid Verification",
      description: "Our automated verification systems ensure lightning-fast processing and disbursements directly to your account.",
      color: "var(--secondary)",
      glow: "rgba(212, 165, 74, 0.2)",
    },
  ];

  const trustPoints = [
    "Transparent Loan Terms",
    "No Hidden Charges",
    "Dedicated Grievance Redressal",
    "RBI Compliant Processes",
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header Reveal
      gsap.from(".why-header > *", {
        y: 40,
        opacity: 0,
        duration: 1.2,
        stagger: 0.15,
        ease: "power4.out",
        scrollTrigger: {
          trigger: ".why-header",
          start: "top 85%",
        }
      });

      // Cards Fluid Reveal
      cardsRef.current.forEach((card, index) => {
        if (card) {
          gsap.from(card, {
            y: 60,
            opacity: 0,
            duration: 1.5,
            delay: index * 0.1,
            ease: "expo.out",
            scrollTrigger: {
              trigger: card,
              start: "top 95%",
            }
          });
        }
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={containerRef} className="w-full py-24 md:py-32 relative overflow-hidden bg-transparent">
      {/* Decorative background element */}
      <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] -translate-y-1/2 -z-10 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[100px] -z-10 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="why-header flex flex-col items-center text-center gap-6 mb-20 md:mb-24">
          <div className="px-5 py-2 rounded-full glass-morphism border border-white/60 text-primary text-[10px] font-black uppercase tracking-[0.3em] shadow-sm">
            Trust & Reliability
          </div>

          <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-text-primary leading-[1.1] tracking-tighter">
            Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary text-glow">Your Prosperity.</span>
          </h2>

          <p className="text-text-secondary text-lg md:text-xl max-w-2xl font-medium leading-relaxed opacity-80">
            As a registered NBFC, we combine regulatory excellence with modern efficiency to deliver the capital you need.
          </p>

          {/* Trust Pills */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mt-4">
            {trustPoints.map((point) => (
              <div key={point} className="flex items-center gap-2 px-4 py-2.5 glass-morphism border border-white/50 rounded-full shadow-sm text-[11px] font-extrabold text-text-primary tracking-wide transition-all hover:bg-white/80">
                <FaCheckCircle className="text-secondary text-xs" />
                {point}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((item, index) => (
            <div
              key={index}
              ref={(el) => { cardsRef.current[index] = el; }}
              className="group relative glass-morphism rounded-[2.5rem] p-10 border border-white/60 shadow-xl hover:shadow-2x-strong transition-all duration-700 hover:-translate-y-4 flex flex-col items-start gap-8 overflow-hidden h-full"
            >
              {/* Subtle Inner Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl transition-all duration-700 group-hover:scale-110 group-hover:rotate-3 shadow-lg relative z-10"
                style={{ backgroundColor: item.color, color: 'white', boxShadow: `0 12px 24px -8px ${item.glow}` }}
              >
                {item.icon}
              </div>

              <div className="relative z-10 space-y-4">
                <h3 className="text-2xl font-black text-text-primary leading-tight tracking-tight group-hover:text-primary transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-text-secondary font-medium text-base leading-relaxed opacity-70 group-hover:opacity-100 transition-all duration-500">
                  {item.description}
                </p>
              </div>

              {/* Progress-style accent line */}
              <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary via-secondary to-primary w-0 group-hover:w-full transition-all duration-700" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
